import { useState, useEffect, useCallback, useMemo } from 'react'
import { fetchNoticesList, deleteNotice } from '../api/noticeApi'
import { appToast } from '../../../../components/AppToast'
import { appConfirm } from '../../../../components/AppConfirmModal'

export default function useNoticeList() {
  const [records, setRecords] = useState([])
  const [loading, setLoading] = useState(false)
  const [searchText, setSearchText] = useState('')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)

  const loadData = useCallback(async () => {
    setLoading(true)
    try {
      const res = await fetchNoticesList(searchText)
      setRecords(res.records)
    } catch (e) {
      console.error('Failed to load notices', e)
      appToast.error('Failed to load notices list.')
    } finally {
      setLoading(false)
    }
  }, [searchText])

  useEffect(() => {
    loadData()
  }, [loadData])

  useEffect(() => {
    setPage(1)
  }, [searchText])

  useEffect(() => {
    const handler = () => loadData()
    window.addEventListener('cloud-notices-updated', handler)
    return () => window.removeEventListener('cloud-notices-updated', handler)
  }, [loadData])

  const totalCount = records.length
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize))

  const pagedRecords = useMemo(() => {
    const start = (page - 1) * pageSize
    return records.slice(start, start + pageSize)
  }, [records, page, pageSize])

  const rangeStart = totalCount === 0 ? 0 : (page - 1) * pageSize + 1
  const rangeEnd = Math.min(totalCount, page * pageSize)

  const goToNextPage = () => {
    setPage((prev) => Math.min(totalPages, prev + 1))
  }

  const goToPreviousPage = () => {
    setPage((prev) => Math.max(1, prev - 1))
  }

  const handleDelete = async (id) => {
    const ok = await appConfirm({
      title: 'Delete Notice',
      message: 'Are you sure you want to delete this entry?',
      confirmText: 'Delete',
      isDestructive: true,
    })
    if (ok) {
      try {
        await deleteNotice(id)
        appToast.success('Entry deleted successfully.')
        loadData()
      } catch (e) {
        console.error('Failed to delete notice', e)
        appToast.error('Error deleting entry.')
      }
    }
  }

  return {
    state: {
      records,
      pagedRecords,
      loading,
      searchText,
      totalCount,
      page,
      pageSize,
      totalPages,
      rangeStart,
      rangeEnd,
    },
    actions: {
      setSearchText,
      setPage,
      setPageSize,
      goToNextPage,
      goToPreviousPage,
      handleDelete,
      loadData,
    },
  }
}
